/**
 * AutomationsCard
 *
 * Settings section showing agent-created automations for the selected scope.
 * Fetches workflows tagged 'agent-automation' from the workflow service
 * and provides enable/disable/delete controls.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Zap,
  Loader2,
  Trash2,
  RefreshCw,
  Wifi,
  Bell,
  Lightbulb,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { FOREST_GREEN } from "@/styles/tokens";
import { defaultWorkflowConfig } from "@/types/workflow-config";

interface Automation {
  id: string;
  name: string;
  description: string;
  status: string;
  pattern?: string;
  instruction?: string;
  scope: AutomationScope;
  createdByUserId?: string;
  orgSlug?: string;
  createdAt?: string;
  lastExecutedAt?: string;
  executionCount?: number;
  nodes?: WorkflowNode[];
}

type AutomationScope = "personal" | "org";
export type AutomationsCardMode = "personal" | "org";

interface AutomationsCardProps {
  orgSlug?: string;
  mode?: AutomationsCardMode;
  scope?: AutomationsCardMode;
}

interface WorkflowNode {
  id?: string;
  type: string;
  config?: Record<string, unknown>;
  renderKey: string;
}

interface WorkflowCustomFields {
  scope?: unknown;
  createdByUserId?: unknown;
  pattern?: unknown;
  instruction?: unknown;
  orgSlug?: unknown;
  organizationSlug?: unknown;
}

interface WorkflowPayload {
  _id?: unknown;
  id?: unknown;
  name?: unknown;
  description?: unknown;
  status?: unknown;
  metadata?: {
    customFields?: WorkflowCustomFields;
  };
  created_by?: unknown;
  createdAt?: unknown;
  lastExecutedAt?: unknown;
  executionCount?: unknown;
  definition?: {
    nodes?: unknown;
  };
}

const ORG_AUTOMATION_FETCH_CONCURRENCY = 4;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" ? value : undefined;
}

function getCreatedById(createdBy: unknown): string | undefined {
  if (typeof createdBy === "string") return createdBy;
  if (!isRecord(createdBy)) return undefined;
  return asString(createdBy._id) ?? asString(createdBy.id);
}

function getCurrentUserId(user: unknown): string | undefined {
  if (!isRecord(user)) return undefined;
  return asString(user.id) ?? asString(user._id);
}

function normalizeScope(scope: unknown): AutomationScope {
  return scope === "personal" ? "personal" : "org";
}

function stableConfigValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stableConfigValue);
  }

  if (!isRecord(value)) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value)
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      .map(([key, childValue]) => [key, stableConfigValue(childValue)]),
  );
}

function getStableNodeSignature(
  type: string,
  config: Record<string, unknown> | undefined,
): string {
  if (!config) return type;
  const serializedConfig = JSON.stringify(stableConfigValue(config));
  return `${type}:${serializedConfig ?? ""}`;
}

function normalizeNodes(nodes: unknown): WorkflowNode[] | undefined {
  if (!Array.isArray(nodes)) return undefined;
  const keyCounts = new Map<string, number>();
  const normalized: WorkflowNode[] = [];

  for (const node of nodes) {
    if (!isRecord(node)) continue;
    const type = asString(node.type);
    if (!type) continue;

    const id = asString(node.id) ?? asString(node._id) ?? asString(node.nodeId);
    const config = isRecord(node.config) ? node.config : undefined;
    const keyBase = id
      ? `node:${id}`
      : `node:${getStableNodeSignature(type, config)}`;
    const duplicateCount = keyCounts.get(keyBase) ?? 0;
    keyCounts.set(keyBase, duplicateCount + 1);
    const renderKey =
      duplicateCount === 0 ? keyBase : `${keyBase}:duplicate-${duplicateCount}`;

    normalized.push({ id, type, config, renderKey });
  }

  return normalized.length > 0 ? normalized : undefined;
}

function isWorkflowPayload(value: unknown): value is WorkflowPayload {
  return isRecord(value);
}

function parseWorkflowListResponse(payload: unknown): WorkflowPayload[] {
  if (Array.isArray(payload)) return payload.filter(isWorkflowPayload);
  if (!isRecord(payload)) return [];
  if (Array.isArray(payload.data)) {
    return payload.data.filter(isWorkflowPayload);
  }
  if (Array.isArray(payload.workflows)) {
    return payload.workflows.filter(isWorkflowPayload);
  }
  return [];
}

function chunkItems<T>(items: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
    items.slice(index * size, index * size + size),
  );
}

function mapInBatches<T, R>(
  items: T[],
  batchSize: number,
  mapper: (item: T) => Promise<R>,
): Promise<R[]> {
  return chunkItems(items, batchSize).reduce<Promise<R[]>>(
    (resultsPromise, batch) =>
      resultsPromise.then(async (results) => {
        const batchResults = await Promise.all(batch.map(mapper));
        return [...results, ...batchResults];
      }),
    Promise.resolve([]),
  );
}

function getBodyErrorMessage(body: unknown): string | null {
  if (!isRecord(body)) return null;
  return asString(body.message) ?? asString(body.error) ?? null;
}

function workflowToAutomation(
  workflow: WorkflowPayload,
  orgSlug: string,
): Automation | null {
  const id = asString(workflow._id) ?? asString(workflow.id);
  if (!id) return null;

  const fields = workflow.metadata?.customFields;
  const createdByUserId =
    asString(fields?.createdByUserId) ?? getCreatedById(workflow.created_by);

  return {
    id,
    name: asString(workflow.name) ?? "Untitled",
    description: asString(workflow.description) ?? "",
    status: asString(workflow.status) ?? "draft",
    pattern: asString(fields?.pattern),
    instruction: asString(fields?.instruction),
    scope: normalizeScope(fields?.scope),
    createdByUserId,
    orgSlug,
    createdAt: asString(workflow.createdAt),
    lastExecutedAt: asString(workflow.lastExecutedAt),
    executionCount: asNumber(workflow.executionCount) ?? 0,
    nodes: normalizeNodes(workflow.definition?.nodes),
  };
}

function getNodeIcon(nodeType: string) {
  if (nodeType === "presence_trigger") return <Wifi className="h-3.5 w-3.5" />;
  if (nodeType === "push_notification") return <Bell className="h-3.5 w-3.5" />;
  if (nodeType.includes("kasa")) return <Lightbulb className="h-3.5 w-3.5" />;
  return <Zap className="h-3.5 w-3.5" />;
}

function describeNode(node: WorkflowNode): string {
  const c = node.config || {};
  const eventType = asString(c.eventType);
  const locationName = asString(c.locationName);
  const action = asString(c.action);
  const conditionType = asString(c.conditionType);
  const duration = asNumber(c.duration);
  switch (node.type) {
    case "presence_trigger":
      if (eventType === "zone_occupied")
        return `Zone occupied${locationName ? ` at ${locationName}` : ""}`;
      if (eventType === "zone_empty")
        return `Zone empty${locationName ? ` at ${locationName}` : ""}`;
      if (eventType === "zone_count_changed")
        return `Zone count changed${locationName ? ` at ${locationName}` : ""}`;
      return `When ${eventType || "moved"}${locationName ? ` at ${locationName}` : ""}`;
    case "push_notification":
      return "Send push notification";
    case "kasa_device_control":
      return `${action || "toggle"} device`;
    case "sun_condition":
      return conditionType === "is_dark"
        ? "If dark"
        : conditionType || "Sun condition";
    case "delay_fixed":
      return `Wait ${duration ?? "?"}s`;
    case "log":
      return "Log";
    default:
      return node.type.replace(/_/g, " ");
  }
}

export const AutomationsCard: React.FC<AutomationsCardProps> = ({
  orgSlug,
  mode,
  scope,
}) => {
  const { user, tokens } = useAuthStore();
  const { currentContext, organizations } = useOrganizationStore();
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const baseUrl =
    defaultWorkflowConfig.workflowEngineUrl?.replace(/\/$/, "") ||
    "/workflow-api";
  const effectiveMode = mode ?? scope ?? (orgSlug ? "org" : "personal");
  const currentUserId = getCurrentUserId(user);
  const authToken = tokens?.accessToken ?? null;

  const fetchOrgAutomations = useCallback(
    async (slug: string, accessToken: string): Promise<Automation[]> => {
      const res = await fetch(
        `${baseUrl}/api/orgs/${slug}/workflows?tags=agent-automation&limit=50`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
        },
      );
      if (!res.ok) return [];

      const payload: unknown = await res.json();
      return parseWorkflowListResponse(payload)
        .map((workflow) => workflowToAutomation(workflow, slug))
        .filter((automation): automation is Automation => automation !== null);
    },
    [baseUrl],
  );

  const loadAutomations = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!authToken) return;

      if (effectiveMode === "org") {
        if (!orgSlug) {
          setAutomations([]);
          return;
        }

        const orgAutomations = await fetchOrgAutomations(orgSlug, authToken);
        setAutomations(
          orgAutomations.filter(
            (automation) =>
              automation.scope === "org" && automation.orgSlug === orgSlug,
          ),
        );
        return;
      }

      // Personal settings preserve the existing cross-org view.
      const orgSlugs = organizations.map((org) => org.slug).filter(Boolean);
      if (orgSlugs.length === 0 && currentContext?.orgSlug) {
        orgSlugs.push(currentContext.orgSlug);
      }

      const automationGroups = await mapInBatches(
        orgSlugs,
        ORG_AUTOMATION_FETCH_CONCURRENCY,
        (slug) =>
          fetchOrgAutomations(slug, authToken).catch(() => {
            // Skip failed orgs
            return [];
          }),
      );
      const allAutomations = automationGroups.flat();

      // Deduplicate by ID
      const seen = new Set<string>();
      const deduped = allAutomations.filter((automation) => {
        if (seen.has(automation.id)) return false;
        seen.add(automation.id);
        return true;
      });

      // Personal settings fetch org workflow routes because personal automations can be stored there.
      const filtered = deduped.filter((automation) => {
        return (
          automation.scope === "personal" &&
          automation.createdByUserId === currentUserId
        );
      });

      setAutomations(filtered);
    } catch (err) {
      console.error("AutomationsCard: Load error", err);
      setAutomations([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    authToken,
    currentContext?.orgSlug,
    currentUserId,
    effectiveMode,
    fetchOrgAutomations,
    orgSlug,
    organizations,
  ]);

  useEffect(() => {
    loadAutomations();
  }, [loadAutomations]);

  const toggleAutomation = useCallback(
    async (id: string, currentStatus: string, orgSlug?: string) => {
      const newStatus = currentStatus === "published" ? "draft" : "published";
      try {
        setTogglingId(id);
        if (!authToken) return;

        const route = orgSlug
          ? `${baseUrl}/api/orgs/${orgSlug}/workflows/${id}`
          : `${baseUrl}/api/workflows/${id}`;

        const res = await fetch(route, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        });

        if (!res.ok) {
          const errData: unknown = await res.json().catch(() => null);
          const msg = getBodyErrorMessage(errData) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        setAutomations((prev) =>
          prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a)),
        );
        toast.success(
          newStatus === "published"
            ? "Automation enabled"
            : "Automation paused",
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Unknown error";
        console.error("AutomationsCard: toggle failed", {
          id,
          orgSlug,
          error: msg,
        });
        toast.error(`Failed to update: ${msg}`);
      } finally {
        setTogglingId(null);
      }
    },
    [authToken, baseUrl],
  );

  const deleteAutomation = useCallback(
    async (id: string, orgSlug?: string) => {
      try {
        setDeletingId(id);
        if (!authToken) return;

        const route = orgSlug
          ? `${baseUrl}/api/orgs/${orgSlug}/workflows/${id}`
          : `${baseUrl}/api/workflows/${id}`;

        const res = await fetch(route, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (!res.ok) {
          const errData: unknown = await res.json().catch(() => null);
          const msg = getBodyErrorMessage(errData) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        setAutomations((prev) => prev.filter((a) => a.id !== id));
        toast.success("Automation deleted");
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Unknown error";
        console.error("AutomationsCard: delete failed", {
          id,
          orgSlug,
          error: msg,
        });
        toast.error(`Failed to delete: ${msg}`);
      } finally {
        setDeletingId(null);
      }
    },
    [authToken, baseUrl],
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Zap
                className="h-5 w-5"
                style={{ color: FOREST_GREEN.DEFAULT }}
              />
              {effectiveMode === "org"
                ? "Organization Automations"
                : "My Automations"}
            </CardTitle>
            <CardDescription>
              {effectiveMode === "org"
                ? "Event-driven automations scoped to this organization"
                : "Event-driven automations created through your agent"}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadAutomations}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading automations...
          </div>
        ) : automations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Zap className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No automations yet</p>
            <p className="text-xs mt-1">
              {effectiveMode === "org"
                ? "Org-scoped automations created through agents will appear here"
                : 'Tell your agent something like "notify me when I change WiFi zones"'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {automations.map((automation) => {
              const isActive = automation.status === "published";
              const isToggling = togglingId === automation.id;
              const isDeleting = deletingId === automation.id;
              const nodes = automation.nodes ?? [];
              const firstNodeRenderKey = nodes.at(0)?.renderKey;

              return (
                <div
                  key={automation.id}
                  className="flex items-start justify-between p-3 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
                >
                  <div className="flex-1 min-w-0 mr-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm truncate">
                        {automation.name.replace(/\s*\([a-z0-9]+\)$/, "")}
                      </span>
                      <Badge
                        variant={isActive ? "default" : "secondary"}
                        className="text-xs shrink-0"
                      >
                        {isActive ? "Active" : "Paused"}
                      </Badge>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {automation.scope === "personal" ? "Personal" : "Org"}
                      </Badge>
                    </div>

                    {/* Flow visualization */}
                    {nodes.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        {nodes.map((node) => (
                          <React.Fragment key={node.renderKey}>
                            {firstNodeRenderKey !== node.renderKey && (
                              <span className="text-muted-foreground/50">
                                →
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 bg-muted/50 rounded px-1.5 py-0.5">
                              {getNodeIcon(node.type)}
                              {describeNode(node)}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    )}

                    {/* Original instruction */}
                    {automation.instruction && (
                      <p className="text-xs text-muted-foreground mt-1.5 italic truncate">
                        "{automation.instruction}"
                      </p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                      {automation.executionCount != null &&
                        automation.executionCount > 0 && (
                          <span>
                            Ran {automation.executionCount} time
                            {automation.executionCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      {automation.createdAt && (
                        <span>
                          Created{" "}
                          {new Date(automation.createdAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch
                      checked={isActive}
                      disabled={isToggling}
                      onCheckedChange={() =>
                        toggleAutomation(
                          automation.id,
                          automation.status,
                          automation.orgSlug,
                        )
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      disabled={isDeleting}
                      onClick={() =>
                        deleteAutomation(automation.id, automation.orgSlug)
                      }
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
